// src/pages/GenerateDraft.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import Layout from "../components/Layout";
import { useNavigate } from "react-router-dom";

function GenerateDraft() {
  // ------------------------------------------------------------------------
  // 1. USER AUTH CHECK
  // ------------------------------------------------------------------------
  const authDataString = localStorage.getItem("authData");
  const parsedAuthData = authDataString ? JSON.parse(authDataString) : null;
  const userId = parsedAuthData?.user?.sub;

  // ------------------------------------------------------------------------
  // 2. ONBOARDING BANNER LOGIC
  // ------------------------------------------------------------------------
  const [showOnboardingBanner, setShowOnboardingBanner] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem("hideOnboardingBanner")) {
      setShowOnboardingBanner(true);
    }
  }, []);

  const handleDismissBanner = () => {
    setShowOnboardingBanner(false);
  };

  const handleHideBannerForever = () => {
    localStorage.setItem("hideOnboardingBanner", "true");
    setShowOnboardingBanner(false);
  };

  // ------------------------------------------------------------------------
  // 3. REMAINDER OF YOUR STATE, HOOKS, ETC.
  // ------------------------------------------------------------------------
  const [intent, setIntent] = useState("");
  const [subject, setSubject] = useState("");
  const [purpose, setPurpose] = useState("");
  const [additionalPoints, setAdditionalPoints] = useState([""]);
  const [persuasionInput, setPersuasionInput] = useState("");
  const [nickname, setNickname] = useState("");

  const [senderWebsite, setSenderWebsite] = useState("");
  const [draftContent, setDraftContent] = useState("");
  const [draftId, setDraftId] = useState("");

  // If you still want to use favorites, keep these; otherwise, remove them:
  const [favoriteDrafts, setFavoriteDrafts] = useState([]);
  const [selectedFavorite, setSelectedFavorite] = useState("");
  const [defaultDraftId, setDefaultDraftId] = useState("");

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isCopied, setIsCopied] = useState(false);

  const [isAutosaving, setIsAutosaving] = useState(false);
  const [autosaveError, setAutosaveError] = useState(null);
  const [showAutosaveSuccess, setShowAutosaveSuccess] = useState(false);
  const autosaveTimeoutRef = useRef(null);

  const navigate = useNavigate();

  // ------------------------------------------------------------------------
  // 4. ENSURE FRESH START ON MOUNT
  // ------------------------------------------------------------------------
  useEffect(() => {
    // Always start fresh — remove any old draft ID or content
    setDraftContent("");
    setDraftId("");
    localStorage.removeItem("draftId");

    if (!userId) {
      setError("You are not logged in. Please log in.");
    }
  }, [userId]);

  // ------------------------------------------------------------------------
  // 5. OPTIONAL: FETCH FAVORITES
  // ------------------------------------------------------------------------
  const fetchFavorites = useCallback(async () => {
    if (!userId) return;
    setIsLoading(true);
    setError(null);

    try {
      const FAVORITES_API_URL =process.env.REACT_APP_FAVORITES_URL;
      const payload = { userId };

      const response = await fetch(FAVORITES_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error("Failed to fetch favorite drafts.");
      }

      const data = await response.json();
      let parsedData = data.body ? JSON.parse(data.body) : [];

      const formattedFavorites = parsedData.map((item) => ({
        draftId: item.id,
        nickname: item.nickName,
        draft: item.draft,
        isDefault: item.isDefault === "Y",
      }));

      setFavoriteDrafts(formattedFavorites);

      // We do NOT auto-load any default draftContent here.
      // We only store the draftId but won't setDraftContent from it
      const defaultDraft = formattedFavorites.find((fav) => fav.isDefault);
      if (defaultDraft) {
        setDefaultDraftId(defaultDraft.draftId);
      } else {
        setDefaultDraftId("");
      }
    } catch (err) {
      setError(err.message || "Error fetching favorites");
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  // If you actually need favorites, keep this; otherwise, remove it:
  useEffect(() => {
    fetchFavorites();
  }, [fetchFavorites]);

  // ------------------------------------------------------------------------
  // 6. SELECT FAVORITE (if you need that feature)
  // ------------------------------------------------------------------------
  const handleSelectFavorite = (selectedId) => {
    if (selectedId === "") {
      handleReset();
      return;
    }
    const selected = favoriteDrafts.find((fav) => fav.draftId === selectedId);
    if (selected) {
      setSelectedFavorite(selectedId);
      setDraftContent(selected.draft);
      setDraftId(selected.draftId);
    }
  };

  // ------------------------------------------------------------------------
  // 7. GENERATE DRAFT
  // ------------------------------------------------------------------------
  const handleGenerate = async () => {
    if (
      !intent.trim() ||
      !subject.trim() ||
      !purpose.trim() ||
      !persuasionInput.trim()
    ) {
      setError("Please fill in all required fields.");
      return;
    }

    if (purpose.length > 1000) {
      setError("Purpose cannot exceed 1000 characters.");
      return;
    }
    if (persuasionInput.length > 1000) {
      setError("Persuasion Input cannot exceed 1000 characters.");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const GENERATE_API_URL =process.env.REACT_APP_GENERATE_URL;
      const payload = {
        userId,
        points: {
          intent,
          subject,
          purpose,
          additional_points: additionalPoints,
          persuasionInput,
        },
        senderWebsite,
      };

      const fetchBody = { body: JSON.stringify(payload) };

      const response = await fetch(GENERATE_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(fetchBody),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText);
      }

      const data = await response.json();
      const responseBody = data.body ? JSON.parse(data.body) : data;

      if (responseBody.draft && responseBody.draftId) {
        setDraftContent(responseBody.draft);
        setDraftId(responseBody.draftId);
        if (responseBody.nickname) {
          setNickname(responseBody.nickname);
        }
        localStorage.setItem("draftId", responseBody.draftId);
      } else {
        throw new Error("Invalid response from generate draft API.");
      }
      setSelectedFavorite("");
    } catch (err) {
      setError(
        "Unable to generate a Draft. Please check your credits remaining in the Settings page."
      );
    } finally {
      setIsLoading(false);
    }
  };

  // ------------------------------------------------------------------------
  // 8. RESET (New Draft)
  // ------------------------------------------------------------------------
  const handleReset = () => {
    setIntent("");
    setSubject("");
    setPurpose("");
    setAdditionalPoints([""]);
    setPersuasionInput("");
    setDraftContent("");
    setSelectedFavorite("");
    setDraftId("");
    setSenderWebsite("");
    localStorage.removeItem("draftId");
    setError(null);

    if (!userId) {
      setError("You are not logged in. Please log in to redeem your code.");
    }
  };

  // ------------------------------------------------------------------------
  // 9. ADDITIONAL POINTS
  // ------------------------------------------------------------------------
  const handleAddPoint = () => setAdditionalPoints([...additionalPoints, ""]);
  const handleRemovePoint = (index) => {
    const points = [...additionalPoints];
    points.splice(index, 1);
    setAdditionalPoints(points);
  };
  const handlePointChange = (index, value) => {
    const points = [...additionalPoints];
    points[index] = value;
    setAdditionalPoints(points);
  };

  // ------------------------------------------------------------------------
  // 10. COPY DRAFT
  // ------------------------------------------------------------------------
  const handleCopy = () => {
    navigator.clipboard
      .writeText(draftContent)
      .then(() => {
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
      })
      .catch(() => {
        setError("Failed to copy draft. Please try again.");
      });
  };

  // ------------------------------------------------------------------------
  // 11. AUTOSAVE
  // ------------------------------------------------------------------------
  useEffect(() => {
    if (!draftId || isLoading) return;

    if (autosaveTimeoutRef.current) {
      clearTimeout(autosaveTimeoutRef.current);
    }

    autosaveTimeoutRef.current = setTimeout(() => {
      const performAutosave = async () => {
        setIsAutosaving(true);
        setAutosaveError(null);

        try {
          const AUTOSAVE_API_URL =process.env.REACT_APP_UPDATE_DRAFT_URL;
          const payload = {
            draftId,
            updatedDraft: draftContent,
            userId,
          };

          const response = await fetch(AUTOSAVE_API_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          });

          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(errorText || "Failed to autosave draft.");
          }

          setShowAutosaveSuccess(true);
          setTimeout(() => setShowAutosaveSuccess(false), 2000);
        } catch (err) {
          setAutosaveError(err.message || "Failed to autosave draft.");
        } finally {
          setIsAutosaving(false);
        }
      };

      performAutosave();
    }, 2000);

    return () => {
      if (autosaveTimeoutRef.current) {
        clearTimeout(autosaveTimeoutRef.current);
      }
    };
  }, [draftContent, draftId, userId, isLoading]);

  // ------------------------------------------------------------------------
  // 12. RENDER
  // ------------------------------------------------------------------------
  return (
    <Layout>
      {/* If you had previously removed the 3-step nav, it remains removed here. */}

      {/* Tailwind "Fancy Banner" */}
      {showOnboardingBanner && (
        <div className="relative isolate flex items-center gap-x-6 overflow-hidden bg-gray-50 px-6 py-2.5 sm:px-3.5 sm:before:flex-1">
          <div
            className="absolute top-1/2 left-[max(-7rem,calc(50%-52rem))] -z-10 -translate-y-1/2 transform-gpu blur-2xl"
            aria-hidden="true"
          >
            <div
              className="aspect-[577/310] w-[36.0625rem] bg-gradient-to-r from-pink-300 to-indigo-300 opacity-30"
              style={{
                clipPath:
                  "polygon(74.8% 41.9%, 97.2% 73.2%, 100% 34.9%, 92.5% 0.4%, 87.5% 0%, 75% 28.6%, 58.5% 54.6%, 50.1% 56.8%, 46.9% 44%, 48.3% 17.4%, 24.7% 53.9%, 0% 27.9%, 11.9% 74.2%, 24.9% 54.1%, 68.6% 100%, 74.8% 41.9%)",
              }}
            />
          </div>
          <div
            className="absolute top-1/2 left-[max(45rem,calc(50%+8rem))] -z-10 -translate-y-1/2 transform-gpu blur-2xl"
            aria-hidden="true"
          >
            <div
              className="aspect-[577/310] w-[36.0625rem] bg-gradient-to-r from-pink-300 to-indigo-300 opacity-30"
              style={{
                clipPath:
                  "polygon(74.8% 41.9%, 97.2% 73.2%, 100% 34.9%, 92.5% 0.4%, 87.5% 0%, 75% 28.6%, 58.5% 54.6%, 50.1% 56.8%, 46.9% 44%, 48.3% 17.4%, 24.7% 53.9%, 0% 27.9%, 11.9% 74.2%, 24.9% 54.1%, 68.6% 100%, 74.8% 41.9%)",
              }}
            />
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <p className="text-sm leading-6 text-gray-900">
              <strong className="font-semibold">Welcome to PreciseDraft!</strong>
              <svg
                viewBox="0 0 2 2"
                className="mx-2 inline h-1 w-1 fill-current"
                aria-hidden="true"
              >
                <circle cx="1" cy="1" r="1" />
              </svg>
              Check out our{" "}
              <a
                href="/onboarding"
                className="text-blue-600 underline font-semibold"
              >
                Onboarding Page
              </a>{" "}
              for guidance.
            </p>
            <button
              type="button"
              onClick={handleHideBannerForever}
              className="flex-none rounded-full bg-gray-900 px-3.5 py-1 text-sm font-semibold text-white shadow-sm hover:bg-gray-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-900"
            >
              Don’t Show Again
            </button>
          </div>
          <div className="flex flex-1 justify-end">
            <button
              type="button"
              className="-m-3 p-3 focus-visible:outline-offset-[-4px]"
              onClick={handleDismissBanner}
            >
              <span className="sr-only">Dismiss</span>
              <svg
                className="h-5 w-5 text-gray-900"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Page Content */}
      <div className="container mx-auto mt-10 max-w-4xl bg-white shadow-md p-10 rounded-md">
        <h1 className="text-xl font-semibold mb-6">Create a New Draft</h1>
        <p className="text-gray-600 mb-10">
          Fill out the details below to create a new draft. You can refer to{" "}
          <a href="/displayDrafts">the Draft Templates</a> for examples.
        </p>

        {draftContent ? (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Draft Content
            </label>
            <div className="relative">
              <textarea
                className="w-full p-4 border rounded-md resize-none"
                rows="8"
                value={draftContent}
                onChange={(e) => setDraftContent(e.target.value)}
                disabled={isLoading}
              />
              <button
                className="absolute top-2 right-2 bg-blue-500 text-white py-1 px-2 rounded-md text-sm hover:bg-blue-600 disabled:bg-blue-300"
                onClick={handleCopy}
                disabled={isLoading || !userId}
              >
                {isCopied ? "Copied!" : "Copy"}
              </button>
            </div>
            {nickname && (
              <p className="mt-2 text-sm text-green-600 font-semibold">
                Draft Nickname: <span className="underline">{nickname}</span>
              </p>
            )}
            {showAutosaveSuccess && (
              <p className="mt-2 text-sm text-gray-500">Draft autosaved</p>
            )}
            {autosaveError && (
              <p className="mt-2 text-sm text-red-500 font-semibold">
                {autosaveError}
              </p>
            )}
          </div>
        ) : (
          <>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                What is the Goal of the Draft? *
              </label>
              <input
                type="text"
                className="w-full p-2 border rounded-md"
                value={intent}
                onChange={(e) => setIntent(e.target.value)}
                placeholder="e.g., Sell products"
                required
                disabled={isLoading}
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Give me a Subject line *
              </label>
              <input
                type="text"
                className="w-full p-2 border rounded-md"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="e.g., Introducing Our New Product Line"
                required
                disabled={isLoading}
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Suggest in points, the Purpose of your email *
              </label>
              <textarea
                className="w-full p-2 border rounded-md"
                rows="10"
                value={purpose}
                onChange={(e) => {
                  if (e.target.value.length <= 1000) {
                    setPurpose(e.target.value);
                  }
                }}
                placeholder="Explain the purpose of the email"
                required
                disabled={isLoading}
                maxLength={1000}
              />
              <p className="text-sm text-gray-500 mt-1">
                {purpose.length}/1000 characters
              </p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                What should we mention about you? Tell us your story *
              </label>
              <textarea
                className="w-full p-2 border rounded-md"
                rows="4"
                value={persuasionInput}
                onChange={(e) => {
                  if (e.target.value.length <= 200) {
                    setPersuasionInput(e.target.value);
                  }
                }}
                placeholder="Tell us what makes you stand out..."
                required
                disabled={isLoading}
                maxLength={200}
              />
              <p className="text-sm text-gray-500 mt-1">
                {persuasionInput.length}/200 characters
              </p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Points (Special offers, Promotions, etc.)
              </label>
              {additionalPoints.map((point, index) => (
                <div key={index} className="flex items-center mb-2">
                  <input
                    type="text"
                    className="flex-grow p-2 border rounded-md"
                    value={point}
                    onChange={(e) => handlePointChange(index, e.target.value)}
                    placeholder={`Point ${index + 1}`}
                    disabled={isLoading}
                  />
                  {additionalPoints.length > 1 && (
                    <button
                      type="button"
                      className="ml-2 text-red-500 hover:text-red-700"
                      onClick={() => handleRemovePoint(index)}
                      disabled={isLoading || !userId}
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                className="text-blue-500 hover:text-blue-700 mt-2"
                onClick={handleAddPoint}
                disabled={isLoading || !userId}
              >
                Add Another Point
              </button>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your website or a URL (Optional)
              </label>
              <input
                type="text"
                className="w-full p-2 border rounded-md"
                value={senderWebsite}
                onChange={(e) => setSenderWebsite(e.target.value)}
                placeholder="e.g., https://www.mybusiness.com"
                disabled={isLoading}
              />
            </div>
          </>
        )}

        {error && <p className="text-red-500 mb-6">{error}</p>}

        {isLoading && (
          <div className="flex items-center mb-6">
            <div className="spinner-border animate-spin h-5 w-5 border-t-2 border-b-2 border-blue-500 rounded-full"></div>
            <span className="ml-2 text-blue-500">Generating...</span>
          </div>
        )}

        {/* BUTTONS: "New Draft" + conditional "Generate Draft" or "All Drafts" */}
        <div className="flex justify-between">
          <button
            className="bg-gray-200 text-gray-700 py-2 px-6 rounded-md hover:bg-gray-300 disabled:bg-gray-100 disabled:text-gray-400"
            onClick={handleReset}
            disabled={isLoading || !userId}
          >
            New Draft
          </button>

          {draftContent ? (
            // Once a draft is generated, show "All Drafts" button
            <button
              className="bg-blue-500 text-white py-2 px-6 rounded-md hover:bg-blue-600 disabled:bg-blue-300"
              onClick={() => navigate("/drafts")}
              disabled={isLoading || !userId}
            >
              All Drafts
            </button>
          ) : (
            // Otherwise, show "Generate Draft" button
            <button
              className="bg-blue-500 text-white py-2 px-6 rounded-md hover:bg-blue-600 disabled:bg-blue-300"
              onClick={handleGenerate}
              disabled={isLoading || !userId}
            >
              Generate Draft
            </button>
          )}
        </div>

        <div className="mt-4">
          <p className="text-sm text-gray-500 italic">
            Disclaimer: PreciseDraft utilizes artificial intelligence to assist
            in drafting documents. While we strive for high accuracy, the tool
            may produce errors. Please ensure you review all drafts thoroughly
            before finalizing.
          </p>
        </div>

        {isAutosaving && (
          <p className="mt-2 text-sm text-blue-500 font-semibold">
            Autosaving...
          </p>
        )}
        {autosaveError && (
          <p className="mt-2 text-sm text-red-500 font-semibold">
            {autosaveError}
          </p>
        )}
      </div>
    </Layout>
  );
}

export default GenerateDraft;
